// =============================================================
// FLOW (9.2a): PM scheduler / Gantt — ported from scripts/perpetuum-desktop-
// sections.jsx (SecScheduler), now reading + WRITING the REAL tasks via /api/v1.
//
// A 14-day Gantt of scheduled tasks (those with a dueDate or a relative-overdue
// signal), grouped by device. Drag a task bar to a day → POST
// /tasks/:id/reschedule (writes tasks.due_date); the server applies the
// weekend-avoidance shift when the toggle is on (Sat/Sun → prior Friday) and
// returns `shifted`. The rolling-forecast slider (0–24 mo) is the threshold for
// how far ahead scheduled work is shown. Bands come from the core relative-
// overdue rule. No synthetic data — every bar is a live task.
// =============================================================

import { useMemo, useRef, useState } from "react";
import { relativeOverdue, type Device, type Task } from "@perpetuum/core";
import { useActionableTasks, useDevices, useRescheduleTask } from "../lib/queries.js";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Icon, ICONS } from "../components/primitives.js";

/** A calendar window starting today, `WINDOW_DAYS` long. */
const WINDOW_DAYS = 14;

interface DayCell {
  date: Date;
  /** Day-of-month label. */
  n: number;
  /** Saturday/Sunday. */
  weekend: boolean;
}

function buildDays(start: Date): DayCell[] {
  const days: DayCell[] = [];
  for (let i = 0; i < WINDOW_DAYS; i++) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const dow = date.getDay();
    days.push({ date, n: date.getDate(), weekend: dow === 0 || dow === 6 });
  }
  return days;
}

/** Days between two dates (calendar, local-midnight). */
function dayIndexOf(date: Date, days: DayCell[]): number {
  const t = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return days.findIndex((d) => d.date.getTime() === t);
}

interface Bar {
  task: Task;
  /** Column index in the window (0…WINDOW_DAYS-1). */
  col: number;
  band: "ok" | "warn" | "crit";
  dueDate: Date;
}

export function Scheduler() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  // Load the ACTIONABLE set only (non-completed). On live data the generic
  // tasks window is dominated by the completed historical backlog, so the
  // scheduler — which only ever shows non-completed tasks with a dueDate —
  // would see zero. Asking the server for the actionable set fixes that.
  const tasksQ = useActionableTasks();
  const devicesQ = useDevices();
  const reschedule = useRescheduleTask();

  const [avoidWeekend, setAvoidWeekend] = useState(true);
  const [forecastMonths, setForecastMonths] = useState(6);
  const [selected, setSelected] = useState<Task | null>(null);
  const dragRef = useRef<string | null>(null);

  const today = useMemo(() => new Date(), []);
  const days = useMemo(() => buildDays(today), [today]);
  const horizon = useMemo(() => {
    const d = new Date(today);
    d.setMonth(d.getMonth() + forecastMonths);
    return d;
  }, [today, forecastMonths]);

  const devices = devicesQ.data ?? [];
  const deviceById = useMemo(() => new Map(devices.map((d) => [d.id, d])), [devices]);

  // Scheduled tasks = non-completed tasks with a dueDate inside the rolling
  // forecast horizon (0–24 mo). Those whose dueDate falls in the visible 14-day
  // window get a Gantt bar; the rest are counted as "further out".
  const scheduled = useMemo(() => {
    return (tasksQ.data ?? []).filter((x) => {
      if (x.isCompleted || x.status === "completed" || x.status === "cancelled") return false;
      if (!x.dueDate) return false;
      const due = new Date(x.dueDate);
      if (isNaN(due.getTime())) return false;
      return due <= horizon;
    });
  }, [tasksQ.data, horizon]);

  const furtherOut = useMemo(
    () => scheduled.filter((x) => dayIndexOf(new Date(x.dueDate!), days) < 0).length,
    [scheduled, days],
  );

  // Group the in-window tasks into device rows of Gantt bars.
  const rows = useMemo(() => {
    const byDevice = new Map<string, Bar[]>();
    for (const task of scheduled) {
      const due = new Date(task.dueDate!);
      const col = dayIndexOf(due, days);
      if (col < 0) continue; // outside the visible window
      const intervalUnits = task.interval?.calendarDays ?? task.interval?.counterHours ?? 0;
      const late = task.dueDays && task.dueDays > 0 ? task.dueDays : 0;
      const ov = intervalUnits > 0 && late > 0 ? relativeOverdue(late, intervalUnits) : null;
      const band: Bar["band"] = ov ? (ov.band === "alarm" ? "crit" : "warn") : "ok";
      const key = task.deviceId ?? "unassigned";
      const arr = byDevice.get(key) ?? [];
      arr.push({ task, col, band, dueDate: due });
      byDevice.set(key, arr);
    }
    return [...byDevice.entries()].map(([deviceId, bars]) => ({
      device: deviceById.get(deviceId) as Device | undefined,
      deviceId,
      bars: bars.sort((a, b) => a.col - b.col),
    }));
  }, [scheduled, days, deviceById]);

  const onDropToDay = async (dayIdx: number) => {
    const taskId = dragRef.current;
    dragRef.current = null;
    if (!taskId) return;
    const target = days[dayIdx];
    if (!target) return;
    if (!can("tasks", "write")) {
      toast(t("scheduler.noPerm"), "warning");
      return;
    }
    // Reschedule to noon UTC of the target day (server may shift off the weekend).
    const dueIso = new Date(Date.UTC(target.date.getFullYear(), target.date.getMonth(), target.date.getDate(), 12)).toISOString();
    try {
      const res = await reschedule.mutateAsync({ taskId, dueDate: dueIso, avoidWeekend });
      if (res.shifted) {
        toast(t("scheduler.shifted"), "info");
      } else {
        toast(t("scheduler.rescheduled", { n: target.n }), "success");
      }
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };

  return (
    <div>
      <h2 className="d-h2">{t("scheduler.title")}</h2>
      <p className="d-sub">{t("scheduler.sub")}</p>

      <div className="cal-toolbar">
        <label className="toggle-line">
          <button
            className={`toggle ${avoidWeekend ? "on" : ""}`}
            aria-pressed={avoidWeekend}
            onClick={() => setAvoidWeekend((a) => !a)}
            data-testid="avoid-weekend"
          >
            <span className="knob" />
          </button>
          {t("scheduler.avoidWeekend")}
        </label>
        <label className="toggle-line" style={{ gap: 14 }}>
          {t("scheduler.forecast")}
          <input
            type="range"
            min={0}
            max={24}
            value={forecastMonths}
            onChange={(e) => setForecastMonths(Number(e.target.value))}
            aria-label={t("scheduler.forecast")}
            data-testid="forecast-slider"
            style={{ accentColor: "var(--brand-primary-fill)" }}
          />
          <span className="mono" style={{ fontWeight: 700, minWidth: 56 }} data-testid="forecast-months">
            {forecastMonths} mo
          </span>
        </label>
      </div>

      <div className="gantt" data-testid="gantt">
        <div className="gantt-head">
          <div className="gantt-corner">{t("scheduler.machine")}</div>
          <div className="gantt-days">
            {days.map((d, i) => (
              <div key={i} className={`gantt-day ${d.weekend ? "we" : ""}`}>
                {d.n}
              </div>
            ))}
          </div>
        </div>

        {tasksQ.isLoading && <div className="ad-none">{t("common.loading")}</div>}
        {!tasksQ.isLoading && rows.length === 0 && <div className="ad-none">{t("scheduler.empty")}</div>}

        {rows.map((r) => (
          <div className="gantt-row" key={r.deviceId}>
            <div className="gantt-label">
              {r.device?.name ?? t("scheduler.unassigned")}
              <small>{r.device?.inventoryNumber ?? r.device?.serialNumber ?? ""}</small>
            </div>
            <div className="gantt-track">
              {days.map((d, i) => (
                <div
                  key={i}
                  className={`gantt-cell ${d.weekend ? "we" : ""}`}
                  data-testid={`gantt-cell-${i}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => void onDropToDay(i)}
                />
              ))}
              {r.bars.map((b) => (
                <div
                  key={b.task.id}
                  className={`gbar ${b.band}`}
                  draggable
                  data-testid={`gbar-${b.task.id}`}
                  role="button"
                  tabIndex={0}
                  title={`${b.task.title} — ${t("scheduler.dragHint")}`}
                  style={{ left: `calc(${(b.col / WINDOW_DAYS) * 100}% + 3px)`, width: `calc(${(1 / WINDOW_DAYS) * 100}% - 6px)` }}
                  onDragStart={() => {
                    dragRef.current = b.task.id;
                  }}
                  onClick={() => setSelected(b.task)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelected(b.task);
                    }
                  }}
                >
                  <span className="gbar-inlabel">{b.task.title}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="note" style={{ marginTop: 16 }}>
        <Icon d={ICONS.alert} size={16} />
        <div>
          {t("scheduler.foot")}
          {furtherOut > 0 && <> {t("scheduler.furtherOut", { n: furtherOut })}</>}
        </div>
      </div>

      {selected && (
        <div className="gd-scrim" onClick={() => setSelected(null)}>
          <aside className="gd-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="gd-head">
              <div className="gd-head-top">
                <button className="gd-close" onClick={() => setSelected(null)} aria-label={t("scheduler.close")}>
                  <Icon d={ICONS.x} size={18} />
                </button>
              </div>
              <h3>{selected.title}</h3>
              <div className="dc-code">{selected.number ?? selected.id.slice(0, 8)}</div>
            </div>
            <div className="gd-body">
              <div className="gd-kv">
                <span className="k">{t("scheduler.scheduled")}</span>
                <span className="v">{selected.dueDate ? new Date(selected.dueDate).toLocaleDateString() : "—"}</span>
              </div>
              <div className="gd-kv">
                <span className="k">{t("scheduler.service")}</span>
                <span className="v">{selected.serviceName ?? "PM"}</span>
              </div>
              <div className="gd-kv">
                <span className="k">{t("scheduler.priority")}</span>
                <span className="v">{selected.priority ?? "normal"}</span>
              </div>
              <p style={{ color: "var(--sys-fg-3)", fontSize: 12.5, marginTop: 12 }}>{t("scheduler.dragToReschedule")}</p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
